import { API_CONFIG } from '../config';
import type { UserContext } from '../config';
import { APIError } from '../types';
import type { ChatMessage, AnthropicRequest, AnthropicResponse, UIMessage } from './types';

export interface StreamCallbacks {
  onStart: (messageId: string) => void;
  onDelta: (text: string) => void;
  onComplete: (fullText: string) => void;
  onError: (error: APIError) => void;
}

const LEGACY_API_DISABLED_ENDPOINT = 'legacy-equanaut-api';
const LEGACY_API_DISABLED_MESSAGE =
  'Equanaut legacy responses API is not configured. Use the Gateway-backed /api/v1/agent paths or select an organization before sending a message.';

function legacyApiDisabledError(): APIError {
  return new APIError({
    status: 503,
    message: LEGACY_API_DISABLED_MESSAGE,
    endpoint: LEGACY_API_DISABLED_ENDPOINT,
  });
}

export const messageService = {
  async sendStream(messages: ChatMessage[], callbacks: StreamCallbacks, userContext?: UserContext): Promise<void> {
    if (!API_CONFIG.BASE_URL) {
      callbacks.onError(legacyApiDisabledError());
      return;
    }

    const url = `${API_CONFIG.BASE_URL}/v1/responses`;
    const fullUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;

    const systemPrompt = API_CONFIG.getSystemPrompt(userContext);

    // Build OpenAI Responses API input from conversation history
    const input: unknown[] = [];
    if (systemPrompt) {
      input.push({ type: 'message', role: 'developer', content: systemPrompt });
    }
    for (var i = 0; i < messages.length; i++) {
      var m = messages[i];
      input.push({ type: 'message', role: m.role, content: m.content });
    }

    const payload = {
      model: 'default',
      input: input,
    };

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (API_CONFIG.GATEWAY_TOKEN) {
      headers['Authorization'] = 'Bearer ' + API_CONFIG.GATEWAY_TOKEN;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(function() { controller.abort(); }, API_CONFIG.REQUEST_TIMEOUT);

    try {
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        var errorMessage = response.statusText;
        try {
          var errorData = await response.json();
          if (errorData && errorData.error && errorData.error.message) {
            errorMessage = errorData.error.message;
          } else if (errorData && errorData.detail) {
            errorMessage = errorData.detail;
          } else if (errorData && errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseErr) {
          // Use status text
        }
        throw new APIError({
          status: response.status,
          message: errorMessage,
          endpoint: '/v1/responses',
        });
      }

      var data = await response.json();

      // Parse OpenAI Responses format: { output: [{ type: "message", content: [{ type: "output_text", text }] }] }
      var fullText = '';
      if (data.output && Array.isArray(data.output)) {
        for (var j = 0; j < data.output.length; j++) {
          var item = data.output[j];
          if (item.type === 'message' && item.content && Array.isArray(item.content)) {
            for (var k = 0; k < item.content.length; k++) {
              var part = item.content[k];
              if (part.type === 'output_text' && part.text) {
                fullText += (fullText ? '\n\n' : '') + part.text;
              }
            }
          }
        }
      }
      if (!fullText) {
        fullText = data.response || 'No response received.';
      }
      var messageId = data.id || ('msg-' + Date.now());

      callbacks.onStart(messageId);
      callbacks.onDelta(fullText);
      callbacks.onComplete(fullText);
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof APIError) {
        callbacks.onError(error);
        return;
      }

      if (error instanceof DOMException && error.name === 'AbortError') {
        callbacks.onError(new APIError({
          status: 408,
          message: 'Request timed out',
          endpoint: '/v1/responses',
        }));
        return;
      }

      callbacks.onError(new APIError({
        status: 500,
        message: error instanceof Error ? error.message : 'Network error',
        endpoint: '/v1/responses',
        cause: error,
      }));
    }
  },

  async send(messages: ChatMessage[], userContext?: UserContext): Promise<UIMessage> {
    if (!API_CONFIG.BASE_URL) {
      throw legacyApiDisabledError();
    }

    const url = `${API_CONFIG.BASE_URL}/messages`;
    const fullUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;

    const payload: AnthropicRequest = {
      model: API_CONFIG.MODEL,
      max_tokens: API_CONFIG.MAX_TOKENS,
      system: API_CONFIG.getSystemPrompt(userContext),
      messages,
    };

    let retryCount = 0;
    const { ATTEMPTS } = API_CONFIG.RETRY_CONFIG;

    while (retryCount <= ATTEMPTS) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.REQUEST_TIMEOUT);

      try {
        const response = await fetch(fullUrl, {
          method: 'POST',
          headers: API_CONFIG.HEADERS,
          body: JSON.stringify(payload),
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        if (!response.ok) {
          let errorMessage = response.statusText;
          try {
            const errorData = await response.json();
            if (errorData && errorData.error && errorData.error.message) {
              errorMessage = errorData.error.message;
            } else if (errorData && errorData.message) {
              errorMessage = errorData.message;
            }
          } catch (parseErr) {
            // Use status text if JSON parsing fails
          }

          if (response.status >= 400 && response.status < 500) {
            throw new APIError({
              status: response.status,
              message: errorMessage,
              endpoint: '/messages',
            });
          }

          throw new APIError({
            status: response.status,
            message: errorMessage,
            endpoint: '/messages',
          });
        }

        const data: AnthropicResponse = await response.json();

        const assistantContent = data.content
          .filter(block => block.type === 'text')
          .map(block => block.text)
          .join('\n');

        return {
          id: data.id,
          role: 'assistant',
          content: assistantContent,
          timestamp: Math.floor(Date.now() / 1000),
        };
      } catch (error) {
        clearTimeout(timeoutId);

        if (error instanceof APIError) {
          throw error;
        }

        if (retryCount === ATTEMPTS) {
          if (error instanceof APIError) throw error;

          if (error instanceof DOMException && error.name === 'AbortError') {
            throw new APIError({
              status: 408,
              message: 'Request timed out',
              endpoint: '/messages',
            });
          }

          throw new APIError({
            status: 500,
            message: error instanceof Error ? error.message : 'Network error',
            endpoint: '/messages',
            cause: error,
          });
        }

        const delay = Math.min(
          API_CONFIG.RETRY_CONFIG.INITIAL_DELAY * Math.pow(2, retryCount),
          API_CONFIG.RETRY_CONFIG.MAX_DELAY
        ) + Math.random() * API_CONFIG.RETRY_CONFIG.JITTER;
        await new Promise(resolve => setTimeout(resolve, delay));
        retryCount++;
      }
    }

    throw new APIError({
      status: 500,
      message: 'Maximum retries exceeded',
      endpoint: '/messages',
    });
  },
};
